import React, { useState, useEffect } from "react";
import { Edit, Delete, Settings, Download } from "lucide-react";
import * as XLSX from 'xlsx';

export default function AllStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [actionMenu, setActionMenu] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/students`,
                {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
        }
      );
      if (!res.ok) {
        throw new Error(`HTTP ${res.status} - ${res.statusText}`);
      }
      const data = await res.json();

      const mapped = data.map((item) => ({
        id: item.student_id,
        name: item.full_name || "N/A",
        email: item.email || "N/A",
        studentId: item.student_id || "N/A",
        university_name: item.university_name || "N/A",
        programme_name: item.programme_name || "N/A",
        intake: item.intake || "N/A",
        status: item.status,
        createdDate: item.created_at || "N/A",
      }));

      setStudents(mapped);
    } catch (err) {
      console.error("Failed to fetch students:", err);
      showNotification("Failed to load students", "error");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleDeleteStudent = async (studentId) => {
  setDeleteConfirm(studentId); // Track which student is being deleted
  setNotification(null); // Clear any previous notifications

  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/catalog/students/${studentId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Include token for authentication
        },
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.message || "Failed to delete student");

    // Update local state
    setStudents(prev => prev.filter(student => student.id !== studentId));
    setNotification({
      message: result.message || "Student deleted successfully",
      type: "success"
    });
    
    // Handle pagination adjustment if needed
    if (currentStudents.length === 1 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  } catch (error) {
    console.error("Error deleting student:", error);
    setNotification({
      message: error.message || "Failed to delete student",
      type: "error"
    });
  } finally {
    setDeleteConfirm(null);
    setActionMenu(null);
  }
};

  const exportToExcel = () => {
    const exportData = filteredStudents.map(student => ({
      'Student ID': student.studentId,
      'Full Name': student.name,
      'Email': student.email,
      'Status': student.status,
      'Created Date': student.createdDate
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "Students_List.xlsx");
    showNotification("Exported to Excel successfully");
  };

  const filteredStudents = students.filter((student) =>
    student.name.toLowerCase().includes(filter.toLowerCase()) ||
    student.email.toLowerCase().includes(filter.toLowerCase()) ||
    student.studentId.toLowerCase().includes(filter.toLowerCase())
  );

  const totalPages = Math.ceil(filteredStudents.length / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const currentStudents = filteredStudents.slice(
    startIdx,
    startIdx + rowsPerPage
  );

  const toggleMenu = (id) => {
    setActionMenu((prev) => (prev === id ? null : id));
    setDeleteConfirm(null);
  };

  const getPageNumbers = (total, current, maxButtons = 5) => {
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(total, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const goToPage = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
    setActionMenu(null);
    
    const container = document.querySelector(".p-6.bg-white.rounded-xl.shadow-lg");
    if (container) {
      container.scrollIntoView({ behavior: "smooth" });
    }
  };

  const renderActionMenu = (student) => {
    
    if (deleteConfirm === student.id) {
      return (
        <div className="absolute z-10 right-0 mt-1 w-48 bg-white border rounded-md shadow-lg">
          <div className="p-2 text-sm text-gray-700">
            Delete {student.name}?
          </div>
          <div className="flex border-t">
            <button
              onClick={() => {
                setDeleteConfirm(null);
              }}
              className="flex-1 px-3 py-2 text-center hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                handleDeleteStudent(student.id);
              }}
              className="flex-1 px-3 py-2 text-center hover:bg-gray-50 text-sm text-red-500 font-medium"
            >
              Confirm
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="absolute z-10 right-0 mt-1 w-36 bg-white border rounded-md shadow-lg">
        <button
          onClick={() => {
            setActionMenu(null);
          }}
          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 w-full text-left text-sm"
        >
          <Edit size={16} className="text-blue-500" /> Edit
        </button>
        <button
          onClick={() => {
            setDeleteConfirm(student.id);
          }}
          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 w-full text-left text-sm text-red-500"
        >
          <Delete size={16} /> Delete
        </button>
      </div>
    );
  };



  if (loading) {
    return (
      <div className="w-full flex items-center justify-center p-10">
        <div className="text-lg text-gray-600">Loading students...</div>
      </div>
    );
  }

  return (
    <div className="w-9xl mx-auto mt-12 bg-white rounded-xl shadow-lg p-6 relative">
      {/* Notification */}
      {notification && (
        <div
          className={`absolute top-4 right-4 px-4 py-2 rounded-md shadow-md z-50 transition-all duration-300 ${
            notification.type === "error" 
              ? "bg-red-100 text-red-800" 
              : "bg-green-100 text-green-800"
          }`}
        >
          {notification.message}
        </div>
      )}

      <div className="border-b pb-3 mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">Student List</h2>
        <p className="text-sm text-gray-500 mt-1">Browse all students</p>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div className="w-full sm:w-auto flex items-center gap-2">
          <input
            type="text"
            placeholder="Search by name, email or ID..."
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full sm:w-64 px-4 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
          
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md shadow text-sm transition-colors"
            title="Export to Excel"
          >
            <Download size={16} />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="rowsPerPage" className="text-sm font-medium text-gray-700">
            Show:
          </label>
          <select
            id="rowsPerPage"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border rounded-md px-3 py-1 text-sm"
          >
            {[5, 10, 25, 50].map((num) => (
              <option key={num} value={num}>
                {num}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm text-gray-700">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-2 text-left">#</th>
              <th className="px-4 py-2 text-left">Full Name</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Student ID</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Programme</th>
              <th className="px-4 py-2 text-left">University</th>
              <th className="px-4 py-2 text-left">Intake</th>
              <th className="px-4 py-2 text-left">Created Date</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentStudents.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-4 text-gray-500">
                  No students found
                </td>
              </tr>
            ) : (
              currentStudents.map((student, index) => (
                <tr key={student.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-2 text-center">{startIdx + index + 1}</td>
                  <td className="px-4 py-2 font-medium text-blue-600">
                    {student.name}
                  </td>
                  <td className="px-4 py-2">{student.email}</td>
                  <td className="px-4 py-2">{student.studentId}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        student.status === "Incomplete Application"
                          ? "bg-yellow-100 text-yellow-800"
                          : student.status === "Complete Application"
                          ? "bg-green-100 text-green-600"
                          : student.status === "Conditional Offer"
                          ? "bg-blue-100 text-blue-600"
                          : student.status === "Rejected"
                          ? "bg-red-100 text-red-600"
                          : student.status === "Defer"
                          ? "bg-purple-100 text-purple-600"
                          : student.status === "Unconditional Offer"
                          ? "bg-green-200 text-green-700"
                          : student.status === "Enroll"
                          ? "bg-blue-100 text-blue-600"
                          : student.status === "Submitted"
                          ? "bg-blue-50 text-blue-600"
                          : student.status === "Cancel"
                          ? "bg-orange-200 text-orange-700"
                          : student.status === "Not Eligible"
                          ? "bg-pink-100 text-pink-600"
                          : student.status === "SFE Exhausted"
                          ? "bg-orange-100 text-orange-600"
                          : student.status === "Alumni"
                          ? "bg-gray-300 text-gray-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {student.status}
                    </span>
                  </td>
                  <td className="px-4 py-2">{student.programme_name}</td>
                  <td className="px-4 py-2">{student.university_name}</td>
                  <td className="px-4 py-2">{student.intake}</td>
                  <td className="px-4 py-2">{student.createdDate}</td>
                  <td className="px-4 py-2 text-center relative">
                    {/* Direct Delete Button for Testing */}
                    <button
                      className="bg-red-500 text-white px-2 py-1 rounded mr-2 text-xs"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (window.confirm(`Are you sure you want to delete ${student.name}?`)) {
                          handleDeleteStudent(student.id);
                        }
                      }}
                    >
                      DELETE
                    </button>
                    
                    {/* Original Settings Button */}
                    <button
                      className="text-gray-500 hover:text-blue-600 transition-colors p-1 border border-gray-300 rounded"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        alert(`Settings clicked for ${student.name}`);
                        toggleMenu(student.id);
                      }}
                      aria-haspopup="true"
                      aria-expanded={actionMenu === student.id}
                    >
                      <Settings size={18} />
                    </button>
                    {actionMenu === student.id && (
                      <div 
                        className="absolute z-50 right-0 mt-1 w-36 bg-white border-2 border-red-500 rounded-md shadow-xl"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: '0',
                          zIndex: 9999
                        }}
                      >
                        <div className="p-2 bg-yellow-100 text-xs">Menu is showing!</div>
                        {renderActionMenu(student)}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
          <span>
            Showing{" "}
            <strong>
              {startIdx + 1}-{Math.min(startIdx + rowsPerPage, filteredStudents.length)}
            </strong>{" "}
            of <strong>{filteredStudents.length}</strong> students
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 border rounded-md disabled:opacity-50"
            >
              ⏮
            </button>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2 py-1 border rounded-md disabled:opacity-50"
            >
              ◀
            </button>
            {getPageNumbers(totalPages, currentPage).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                className={`px-3 py-1 min-w-[36px] border rounded-md ${
                  currentPage === pageNum
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                {pageNum}
              </button>
            ))}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 border rounded-md disabled:opacity-50"
            >
              ▶
            </button>
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 border rounded-md disabled:opacity-50"
            >
              ⏭
            </button>
          </div>
        </div>
      )}
    </div>
  );
}